// @ts-check
import { gql, useQuery } from '@apollo/client';
import React from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import { Trans, t, Plural } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { IconClock, IconArrowRight } from '@tabler/icons-react';
import { Box, Flex, Text } from '~/design-system';

const IN_PROGRESS_QUERY = gql`
  query SmartImportPickerInProgress($workspaceId: ID!) {
    workspace(workspaceId: $workspaceId) {
      workspaceId
      dataImports(filter: { state: { in: ["JOB_IN_PROGRESS", "STARTED"] } }) {
        nodes {
          importId
          name
          type
          createdAt
        }
      }
    }
  }
`;

export default function InProgressBanner() {
  const { i18n } = useLingui();
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const { data } = useQuery(IN_PROGRESS_QUERY, {
    variables: { workspaceId },
  });

  const imports: Array<{
    importId: string;
    name?: string | null;
    type?: string | null;
    createdAt?: string | null;
  }> = (data?.workspace?.dataImports?.nodes ?? []).filter(Boolean);

  if (imports.length === 0) return null;

  // Sort newest-first by createdAt (the server doesn't guarantee order in
  // this query). Copy first — don't mutate the apollo cache result.
  const sorted = [...imports].sort((a, b) => {
    const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
    const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
    return tb - ta;
  });
  const recent = sorted.slice(0, 3);
  const more = sorted.length - recent.length;

  return (
    <Box
      sx={{
        borderRadius: 10,
        border: '1px solid #fde68a',
        backgroundColor: '#fffbeb',
        padding: 3,
      }}
    >
      <Flex sx={{ alignItems: 'center', gap: 2, marginBottom: 2 }}>
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: 6,
            backgroundColor: '#fef3c7',
            color: '#b45309',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <IconClock size={16} stroke={1.75} />
        </Box>
        <Text sx={{ fontSize: '14px', fontWeight: 600, color: '#92400e' }}>
          <Plural
            value={imports.length}
            one="# import in progress"
            other="# imports in progress"
          />
        </Text>
      </Flex>
      <Flex sx={{ flexDirection: 'column', gap: 1 }}>
        {recent.map((oneImport) => (
          <Flex
            key={oneImport.importId}
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: 2,
              borderRadius: 6,
              backgroundColor: 'white',
              border: '1px solid #fde68a',
            }}
          >
            <Text sx={{ fontSize: '13px', color: '#374151' }}>
              {oneImport.name || i18n._(t`Untitled import`)}
            </Text>
            <Box
              as={RouterLink}
              // @ts-ignore Box doesn't type 'to'
              to={`/${workspaceId}/${oneImport.type}/${oneImport.importId}`}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                fontSize: '13px',
                color: '#2563eb',
                textDecoration: 'none',
                fontWeight: 600,
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              <Trans>Continue</Trans>
              <IconArrowRight size={14} stroke={2} />
            </Box>
          </Flex>
        ))}
        {more > 0 && (
          <Box
            as={RouterLink}
            // @ts-ignore Box doesn't type 'to'
            to={`/${workspaceId}/history/inProgress`}
            sx={{
              fontSize: '12px',
              color: '#92400e',
              textDecoration: 'none',
              marginTop: 1,
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            <Trans>+ {more} more — see all</Trans>
          </Box>
        )}
      </Flex>
    </Box>
  );
}
